/**
 * RemoveAreaButton - Danger zone section with area deletion
 *
 * Self-contained component that renders a destructive "Remove Area" button
 * with an AlertDialog confirmation. Placed at the bottom of builder forms
 * in edit mode only.
 */

import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeleteHangar } from '@/hooks/useHangars'

interface RemoveAreaButtonProps {
  /** ID of the area to delete */
  areaId: string
  /** Display name of the area (for confirmation message) */
  areaName: string
  /** Called after successful deletion (to close editor, reset store) */
  onRemoved: () => void
  /**
   * Compact mode renders a single small destructive button (for footer rows)
   * instead of the full-width "Danger Zone" section.
   */
  compact?: boolean
}

export function RemoveAreaButton({ areaId, areaName, onRemoved, compact = false }: RemoveAreaButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false)
  const deleteMutation = useDeleteHangar()

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(areaId)
      setShowConfirm(false)
      onRemoved()
    } catch {
      // Error is shown via toast in the mutation hook
    }
  }

  return (
    <>
      {compact ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => setShowConfirm(true)}
          disabled={deleteMutation.isPending}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          {deleteMutation.isPending ? 'Removing...' : 'Delete'}
        </Button>
      ) : (
        <>
          <Separator />

          <div className="space-y-3">
            <h4 className="font-medium text-sm text-destructive">Danger Zone</h4>
            <p className="text-xs text-muted-foreground">
              Permanently remove this area and all its units. This action cannot be undone.
            </p>
            <Button
              type="button"
              variant="destructive"
              className="w-full"
              onClick={() => setShowConfirm(true)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleteMutation.isPending ? 'Removing...' : 'Remove Area'}
            </Button>
          </div>
        </>
      )}

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Area</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>"{areaName}"</strong>?
              <br />
              <br />
              This will also remove all associated units and clean up aircraft
              positions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Removing...' : 'Remove Area'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
